import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CaretDown, SpinnerGap, Warning } from '@phosphor-icons/react';
import {
    getCrossPatternConfig,
    getCrossPatternsPending,
    getCrossPatternsAccepted,
    getCrossPatternsRejected,
    agreeCrossPattern,
    overrideCrossPattern,
} from '@/lib/api';
import { INTENT_TO_BUTTON } from '@/config/task-display-config';
import {
    DESIGN_INTENTS,
    DS_BORDERS,
    DS_COLORS,
    DS_SPACING,
    DS_TYPOGRAPHY,
} from '@/config/ui-invariants';
import { toast } from 'sonner';

/**
 * Cross-Sector Pattern Agent — CEO Idea Card inbox.
 * Authority: docs/agents/cross_sector_pattern/CROSS_SECTOR_PATTERN_AGENT_SPEC.md
 * Route: /agents/cross-sector-patterns
 *
 * Three tabs (pending/accepted/rejected) over POST /api/cross-sector-patterns/run output.
 * Action verbs from Spec §6: Agree (calibration rating 1-5) or Override (rejection reason 1-3).
 * Both selectors are populated from GET /api/cross-sector-patterns/config — never hardcoded.
 */

const TABS = [
    { id: 'pending', label: 'Pending' },
    { id: 'accepted', label: 'Accepted' },
    { id: 'rejected', label: 'Rejected' },
];

const MODAL_TITLES = {
    agree: 'Calibrate this pattern',
    override: 'Why are you overriding this pattern?',
};

// Field order for the refresh-card change-context block (Spec §11 Fix 3). The
// section labels are served from config (change_context_labels) so no hardcoded
// UI label literals live in the component.
const CHANGE_CONTEXT_FIELD_KEYS = ['what_changed', 'why_significant', 'thesis_implication'];

// Fallback label if config has no entry for a key: "what_changed" → "What Changed".
function humanizeFieldKey(key) {
    return String(key).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * Split the agent's accrezeo_analysis string into per-question answers.
 * The agent writes a fixed "Q1.\n\n<answer>\n\nQ2.\n\n<answer>…" format.
 * Returns [{ key: "1", answer }, …]. Returns [] when the text is not in that
 * format, so callers can fall back to rendering the raw block.
 */
function parseQuestionAnswers(analysis) {
    if (!analysis || typeof analysis !== 'string') return [];
    // Capturing split: parts = [preamble, "1", answer1, "2", answer2, …].
    const parts = analysis.split(/(?:^|\n)\s*Q\s*([1-5])\s*[.):]\s*/);
    const out = [];
    for (let i = 1; i < parts.length; i += 2) {
        const key = parts[i];
        const answer = (parts[i + 1] || '').trim();
        if (key) out.push({ key, answer });
    }
    return out;
}


/* ─────────────────────────────────────────────── TabChips ── */

function TabChips({ activeTab, onTabChange, tabCounts }) {
    return (
        <div className="flex items-center gap-2 mb-6">
            {TABS.map(tab => {
                const isActive = activeTab === tab.id;
                const count = tabCounts[tab.id] || 0;
                const intent = isActive ? INTENT_TO_BUTTON.primary : INTENT_TO_BUTTON.neutral;
                return (
                    <button
                        key={tab.id}
                        onClick={() => onTabChange(tab.id)}
                        className={`inline-flex items-center gap-2 px-5 rounded-full font-semibold transition-colors border ${intent.bg} ${intent.text} ${intent.border} ${!isActive ? intent.hover : ''}`}
                        style={{
                            minHeight: DS_SPACING.minTouchTarget + 'px',
                            fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                            fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                            fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                        }}
                    >
                        {tab.label}
                        {count > 0 && (
                            <span
                                className="inline-flex items-center justify-center px-2 py-0.5 rounded-full"
                                style={{
                                    minWidth: '24px',
                                    background: isActive ? 'rgba(255,255,255,0.2)' : DS_COLORS.surface.warm,
                                    fontSize: '12px',
                                    fontWeight: 700,
                                }}
                            >
                                {count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}


/* ─────────────────────────────────────────── Modal Selector ── */

function ModalSelector({ open, title, options, onSelect, onClose, busy }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
            onClick={onClose}
        >
            <div
                className="rounded-lg p-6"
                style={{
                    background: DS_COLORS.surface.white,
                    border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                    boxShadow: DS_COLORS.shadow.card,
                    minWidth: '420px',
                    maxWidth: '600px',
                }}
                onClick={e => e.stopPropagation()}
            >
                <h3
                    className="mb-4"
                    style={{
                        fontFamily: DS_TYPOGRAPHY.listItemTitle.fontFamily,
                        fontSize: DS_TYPOGRAPHY.listItemTitle.fontSize,
                        fontWeight: DS_TYPOGRAPHY.listItemTitle.fontWeight,
                        color: DS_COLORS.text.primary,
                    }}
                >
                    {title}
                </h3>
                <div className="flex flex-col gap-2">
                    {options.map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => onSelect(opt.value)}
                            disabled={busy}
                            className={`text-left px-4 py-3 rounded-lg border transition-colors ${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} disabled:opacity-50`}
                            style={{
                                minHeight: DS_SPACING.minTouchTarget + 'px',
                                fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                <div className="flex justify-end mt-4">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className={`px-4 py-2 rounded-lg ${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} border`}
                        style={{
                            minHeight: DS_SPACING.minTouchTarget + 'px',
                            fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                            fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                            fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────── Pattern Card ── */

function PatternCard({ pattern, ratingOptions, reasonOptions, onAgreed, onOverridden, readOnly, questionLabels, changeContextLabels }) {
    const [expanded, setExpanded] = useState(false);
    const [busy, setBusy] = useState(null);
    const [showAgree, setShowAgree] = useState(false);
    const [showOverride, setShowOverride] = useState(false);

    const labels = questionLabels || {};
    const ccLabels = changeContextLabels || {};
    const questionAnswers = useMemo(
        () => parseQuestionAnswers(pattern.accrezeo_analysis),
        [pattern.accrezeo_analysis],
    );
    const changeContext = pattern.is_refresh_card ? (pattern.change_context || {}) : null;

    const handleAgree = useCallback(async (rating) => {
        setBusy('agree');
        try {
            await agreeCrossPattern(pattern.linked_kb_id, rating);
            toast.success('Pattern agreed and saved.');
            onAgreed && onAgreed(pattern.id);
        } catch (e) {
            toast.error('Could not save — please try again.');
        } finally {
            setBusy(null);
            setShowAgree(false);
        }
    }, [pattern, onAgreed]);

    const handleOverride = useCallback(async (reason) => {
        setBusy('override');
        try {
            await overrideCrossPattern(pattern.linked_kb_id, reason);
            toast.success('Pattern marked as override.');
            onOverridden && onOverridden(pattern.id);
        } catch (e) {
            toast.error('Could not save — please try again.');
        } finally {
            setBusy(null);
            setShowOverride(false);
        }
    }, [pattern, onOverridden]);

    return (
        <div
            className="rounded-lg overflow-hidden mb-4"
            style={{
                background: expanded ? DS_COLORS.surface.white : DS_COLORS.surface.warm,
                borderLeft: `4px solid ${expanded ? DS_BORDERS.color : DS_COLORS.border.brand}`,
                borderTop: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                borderRight: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                borderBottom: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                boxShadow: expanded ? DS_COLORS.shadow.card : 'none',
            }}
        >
            {/* Header — collapsible */}
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full text-left flex items-center justify-between"
                style={{
                    padding: `0 ${DS_SPACING.cardPadding}px`,
                    minHeight: DS_SPACING.minTouchTarget + 'px',
                    paddingTop: '14px',
                    paddingBottom: '14px',
                }}
            >
                <div className="flex-1 min-w-0">
                    <div
                        className="truncate"
                        style={{
                            fontFamily: DS_TYPOGRAPHY.listItemTitle.fontFamily,
                            fontSize: DS_TYPOGRAPHY.listItemTitle.fontSize,
                            fontWeight: DS_TYPOGRAPHY.listItemTitle.fontWeight,
                            color: DS_COLORS.text.primary,
                        }}
                    >
                        {pattern.title || 'Untitled pattern'}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span
                            className={`inline-flex items-center px-2 py-1 rounded-full border ${DESIGN_INTENTS.info.bg} ${DESIGN_INTENTS.info.text} ${DESIGN_INTENTS.info.border}`}
                            style={{ fontSize: '12px', fontWeight: 700 }}
                        >
                            {pattern.evidence_count} evidence
                        </span>
                        <span
                            className={`inline-flex items-center px-2 py-1 rounded-full border ${DESIGN_INTENTS.neutral.bg} ${DESIGN_INTENTS.neutral.text} ${DESIGN_INTENTS.neutral.border}`}
                            style={{ fontSize: '12px', fontWeight: 700 }}
                        >
                            {(pattern.sector_l1_tags || []).length} sectors
                        </span>
                        <span
                            className={`inline-flex items-center px-2 py-1 rounded-full border ${DESIGN_INTENTS.positive.bg} ${DESIGN_INTENTS.positive.text} ${DESIGN_INTENTS.positive.border}`}
                            style={{ fontSize: '12px', fontWeight: 700 }}
                        >
                            {pattern.consensus_survival_count}/{pattern.consensus_run_count} consensus
                        </span>
                        {pattern.edge_case_flag && (
                            <span
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${DESIGN_INTENTS.warning.bg} ${DESIGN_INTENTS.warning.text} ${DESIGN_INTENTS.warning.border}`}
                                style={{ fontSize: '12px', fontWeight: 700 }}
                            >
                                <Warning weight="bold" className="w-3 h-3" /> edge case
                            </span>
                        )}
                    </div>
                </div>
                <CaretDown
                    weight="bold"
                    className="ml-4 flex-shrink-0"
                    style={{
                        width: '20px',
                        height: '20px',
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        color: DS_COLORS.text.secondary,
                    }}
                />
            </button>

            {/* Expanded body */}
            {expanded && (
                <div style={{ padding: `0 ${DS_SPACING.cardPadding}px ${DS_SPACING.cardPadding}px` }}>
                    {/* Refresh-card change context — hidden on first-time cards (Spec §11 Fix 3). */}
                    {changeContext && (
                        <div
                            className="mb-4 p-4 rounded"
                            style={{
                                background: DS_COLORS.surface.warm,
                                border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                                borderLeft: `4px solid ${DS_COLORS.border.brand}`,
                            }}
                        >
                            {CHANGE_CONTEXT_FIELD_KEYS.map(fieldKey => (
                                <div key={fieldKey} style={{ marginBottom: '12px' }}>
                                    <div
                                        style={{
                                            fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                                            fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
                                            fontWeight: DS_TYPOGRAPHY.sectionLabel.fontWeight,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1em',
                                            color: DS_COLORS.text.brand,
                                            marginBottom: '4px',
                                        }}
                                    >
                                        {ccLabels[fieldKey] || humanizeFieldKey(fieldKey)}
                                    </div>
                                    <div
                                        style={{
                                            fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                            fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                            lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                                            color: DS_COLORS.text.primary,
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {changeContext[fieldKey] || '—'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {pattern.content_summary && (
                        <p
                            style={{
                                fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                                color: DS_COLORS.text.primary,
                                marginBottom: DS_SPACING.paragraphSpacing + 'px',
                            }}
                        >
                            {pattern.content_summary}
                        </p>
                    )}
                    {/* Each answer rendered under its question (Spec §11 Fix 1). Question text
                        comes from config (card_question_labels) so wording can change without a
                        frontend deploy. Falls back to the raw block when the analysis is not in
                        the Q1-Q5 format (e.g. older cards). */}
                    {/* DECISION: the question is rendered as a readable bold brand-coloured label
                        (DM Sans, body size) rather than a literal uppercase "section-label", because
                        the questions are full sentences and the CEO is visually impaired — uppercase
                        letter-spaced small text would defeat the purpose of showing the question. */}
                    {questionAnswers.length > 0 ? (
                        <div style={{ marginBottom: DS_SPACING.paragraphSpacing + 'px' }}>
                            {questionAnswers.map(qa => (
                                <div key={qa.key} style={{ marginBottom: '16px' }}>
                                    <div
                                        style={{
                                            fontFamily: DS_TYPOGRAPHY.listItemTitle.fontFamily,
                                            fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                            fontWeight: 700,
                                            lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                                            color: DS_COLORS.text.brand,
                                            marginBottom: '6px',
                                        }}
                                    >
                                        {labels[qa.key] || `Q${qa.key}`}
                                    </div>
                                    <div
                                        style={{
                                            fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                            fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                            lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                                            color: DS_COLORS.text.primary,
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {qa.answer}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div
                            style={{
                                fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                                color: DS_COLORS.text.primary,
                                whiteSpace: 'pre-wrap',
                                marginBottom: DS_SPACING.paragraphSpacing + 'px',
                            }}
                        >
                            {pattern.accrezeo_analysis}
                        </div>
                    )}
                    {pattern.counter_argument && (
                        <div
                            className="mt-4 p-3 rounded"
                            style={{
                                background: DS_COLORS.surface.warm,
                                border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
                            }}
                        >
                            <div
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
                                    fontWeight: DS_TYPOGRAPHY.sectionLabel.fontWeight,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    color: DS_COLORS.text.secondary,
                                    marginBottom: '6px',
                                }}
                            >
                                Counter-argument
                            </div>
                            <div
                                style={{
                                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                                    color: DS_COLORS.text.primary,
                                }}
                            >
                                {pattern.counter_argument}
                            </div>
                        </div>
                    )}

                    {/* Decision metadata */}
                    {(pattern.feedback_rating || pattern.rejection_reason) && (
                        <div
                            className="mt-4"
                            style={{
                                fontFamily: DS_TYPOGRAPHY.meta.fontFamily,
                                fontSize: DS_TYPOGRAPHY.meta.fontSize,
                                color: DS_COLORS.text.secondary,
                            }}
                        >
                            {pattern.feedback_rating && <>Calibration: {pattern.feedback_rating}/5</>}
                            {pattern.rejection_reason && <>Reason: {pattern.rejection_reason}</>}
                        </div>
                    )}

                    {/* Action bar (only on pending tab) */}
                    {!readOnly && (
                        <div
                            className="flex items-center gap-3 mt-6 pt-4"
                            style={{ borderTop: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}` }}
                        >
                            <button
                                onClick={() => setShowOverride(true)}
                                disabled={!!busy}
                                className={`inline-flex items-center gap-2 px-4 rounded-md border ${INTENT_TO_BUTTON.negative.bg} ${INTENT_TO_BUTTON.negative.text} ${INTENT_TO_BUTTON.negative.border} ${INTENT_TO_BUTTON.negative.hover} disabled:opacity-50`}
                                style={{
                                    minHeight: DS_SPACING.minTouchTarget + 'px',
                                    borderRadius: DS_BORDERS.radiusButton,
                                    fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                                    fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                                }}
                            >
                                {busy === 'override' ? <SpinnerGap className="w-4 h-4 animate-spin" /> : null}
                                Override
                            </button>
                            <div className="flex-1" />
                            <button
                                onClick={() => setShowAgree(true)}
                                disabled={!!busy}
                                className={`inline-flex items-center gap-2 px-5 rounded-md border ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-50`}
                                style={{
                                    minHeight: DS_SPACING.minTouchTarget + 'px',
                                    borderRadius: DS_BORDERS.radiusButton,
                                    fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                                    fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                                    fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                                }}
                            >
                                {busy === 'agree' ? <SpinnerGap className="w-4 h-4 animate-spin" /> : null}
                                Agree
                            </button>
                        </div>
                    )}
                </div>
            )}

            <ModalSelector
                open={showAgree}
                title={MODAL_TITLES.agree}
                options={ratingOptions}
                onSelect={handleAgree}
                onClose={() => setShowAgree(false)}
                busy={busy === 'agree'}
            />
            <ModalSelector
                open={showOverride}
                title={MODAL_TITLES.override}
                options={reasonOptions}
                onSelect={handleOverride}
                onClose={() => setShowOverride(false)}
                busy={busy === 'override'}
            />
        </div>
    );
}


/* ─────────────────────────────────────────────── Page ── */

export default function CrossSectorPatternAgent() {
    const [activeTab, setActiveTab] = useState('pending');
    const [pending, setPending] = useState([]);
    const [accepted, setAccepted] = useState([]);
    const [rejected, setRejected] = useState([]);
    const [config, setConfig] = useState({ feedback_rating_scale: {}, rejection_reasons: [], card_question_labels: {}, change_context_labels: {} });
    const [loading, setLoading] = useState(true);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [cfg, p, a, r] = await Promise.allSettled([
                getCrossPatternConfig(),
                getCrossPatternsPending(),
                getCrossPatternsAccepted(),
                getCrossPatternsRejected(),
            ]);
            if (cfg.status === 'fulfilled') setConfig(cfg.value || { feedback_rating_scale: {}, rejection_reasons: [], card_question_labels: {}, change_context_labels: {} });
            if (p.status === 'fulfilled') setPending(p.value?.patterns || []);
            if (a.status === 'fulfilled') setAccepted(a.value?.patterns || []);
            if (r.status === 'fulfilled') setRejected(r.value?.patterns || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const ratingOptions = useMemo(() => {
        const scale = config.feedback_rating_scale || {};
        return Object.keys(scale).sort().map(k => ({ value: parseInt(k, 10), label: `${k} — ${scale[k]}` }));
    }, [config]);

    const reasonOptions = useMemo(() => {
        return (config.rejection_reasons || []).map(r => ({ value: r, label: r }));
    }, [config]);

    const tabCounts = useMemo(() => ({
        pending: pending.length,
        accepted: accepted.length,
        rejected: rejected.length,
    }), [pending, accepted, rejected]);

    const renderTab = () => {
        const list =
            activeTab === 'accepted' ? accepted :
                activeTab === 'rejected' ? rejected : pending;
        if (loading) {
            return (
                <div className="flex items-center justify-center py-16" style={{ color: DS_COLORS.text.secondary }}>
                    <SpinnerGap className="w-6 h-6 animate-spin mr-3" /> Loading patterns…
                </div>
            );
        }
        if (list.length === 0) {
            return (
                <div
                    className="text-center py-16"
                    style={{
                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                        fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                        color: DS_COLORS.text.secondary,
                    }}
                >
                    No patterns in this tab yet.
                </div>
            );
        }
        return list.map(p => (
            <PatternCard
                key={p.id}
                pattern={p}
                ratingOptions={ratingOptions}
                reasonOptions={reasonOptions}
                questionLabels={config.card_question_labels || {}}
                changeContextLabels={config.change_context_labels || {}}
                readOnly={activeTab !== 'pending'}
                onAgreed={() => loadAll()}
                onOverridden={() => loadAll()}
            />
        ));
    };

    return (
        <div>
            <div className="mb-6">
                <h1
                    style={{
                        fontFamily: DS_TYPOGRAPHY.pageTitle.fontFamily,
                        fontSize: DS_TYPOGRAPHY.pageTitle.fontSize,
                        fontWeight: DS_TYPOGRAPHY.pageTitle.fontWeight,
                        letterSpacing: DS_TYPOGRAPHY.pageTitle.letterSpacing,
                        color: DS_COLORS.text.brand,
                        marginBottom: '8px',
                    }}
                >
                    Cross-Sector Patterns
                </h1>
                <p
                    style={{
                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                        fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                        color: DS_COLORS.text.secondary,
                    }}
                >
                    Wednesday-only mechanic synthesis. Agree to anchor a pattern; Override if it does not hold up.
                </p>
            </div>
            <TabChips activeTab={activeTab} onTabChange={setActiveTab} tabCounts={tabCounts} />
            {renderTab()}
        </div>
    );
}
